import apiClient from './apiClient';
import { AuditEventDto, LoginActivityDto, PageResponseDto } from '../types/actionTypes';

export interface AuditEventsQuery {
  page?: number;
  size?: number;
  sort?: string;
  search?: string;
  eventType?: string;
  outcome?: string;
  severity?: string;
  sourceComponent?: string;
  actorName?: string;
  actionId?: number;
  rtuId?: number;
  fromUtc?: string;
  toUtc?: string;
}

export interface LoginActivityQuery {
  page?: number;
  size?: number;
  sort?: string;
  search?: string;
  username?: string;
  sourceIp?: string;
  loginType?: string;
  rtuId?: number;
  fromUtc?: string;
  toUtc?: string;
}

const AUDIT_ENDPOINTS = {
  EVENTS: '/audit/events',
  LOGIN_ACTIVITY: '/audit/login-activity',
} as const;

const toQueryParams = <T extends object>(query: T): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(query as Record<string, string | number | undefined | null>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    const apiKey = key === 'fromUtc' ? 'from' : key === 'toUtc' ? 'to' : key;
    params.set(apiKey, String(value));
  });
  return params;
};

const withPageFallback = <T,>(data: unknown, fallbackPage = 0, fallbackSize = 0): PageResponseDto<T> => {
  if (Array.isArray(data)) {
    return {
      content: data as T[],
      page: fallbackPage,
      size: fallbackSize || data.length,
      totalElements: data.length,
      totalPages: 1,
    };
  }

  const typed = data as Partial<PageResponseDto<T>> | null | undefined;
  return {
    content: typed?.content ?? [],
    page: typed?.page ?? fallbackPage,
    size: typed?.size ?? fallbackSize,
    totalElements: typed?.totalElements ?? typed?.content?.length ?? 0,
    totalPages: typed?.totalPages ?? 0,
  };
};

export const auditService = {
  async fetchAuditEvents(query: AuditEventsQuery = {}): Promise<PageResponseDto<AuditEventDto>> {
    const response = await apiClient.get(AUDIT_ENDPOINTS.EVENTS, {
      params: toQueryParams(query),
    });
    return withPageFallback<AuditEventDto>(response.data, query.page ?? 0, query.size ?? 0);
  },

  async fetchLoginActivity(query: LoginActivityQuery = {}): Promise<PageResponseDto<LoginActivityDto>> {
    const response = await apiClient.get(AUDIT_ENDPOINTS.LOGIN_ACTIVITY, {
      params: toQueryParams(query),
    });
    return withPageFallback<LoginActivityDto>(response.data, query.page ?? 0, query.size ?? 0);
  },
};

export default auditService;
